/**
 * One way to turn a failed request into a message a person can act on. Every
 * request helper (lib/api.ts, explorerApi, auditApi, rankApi, keywordsApi,
 * brandRadarApi, creator/api, push, billing/UpgradePlan) uses it, so:
 *
 *  - the server's own message ("detail") is shown when it is a sentence;
 *  - validation errors (a list) become "Field: what's wrong";
 *  - no message → a plain sentence for the status (session ended, too many
 *    requests, our side failed, Rankbist is updating…), never "[object Object]"
 *    or "Internal Server Error";
 *  - no connection → "Couldn't reach Rankbist…" (status 0);
 *  - an empty successful answer (204) doesn't crash the caller.
 *
 * The thrown error keeps the HTTP status (`status`) and the server's error id
 * (`errorId`, from the API's crash handler) so pages can branch on them and
 * support can find the log line.
 */

export class ApiError extends Error {
  status: number;
  errorId?: string;
  /** Validation problems per field (`{ topic: "Keep this under 120 characters." }`), so forms can show each one under its field. */
  fields: Record<string, string>;
  /** "prelaunch" or "maintenance" when the site refused the request for that reason. */
  code?: string;
  constructor(message: string, status: number, errorId?: string, fields: Record<string, string> = {}, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorId = errorId;
    this.fields = fields;
    this.code = code;
  }
}

/** Pre-registration / maintenance refusals are announced to the page
 *  (components/app/AccessNotice.tsx shows the pop-up, the shell re-reads the account). */
export const ACCESS_EVENT = "rb:access";
/** Fired for every 402/429 answer: a package wall or a limit/allowance that ran out (components/app/LimitNotice.tsx
 *  shows it clearly, wherever the action was started from, so nobody wonders why nothing happened). */
export const LIMIT_EVENT = "rb:limit";

/** Pydantic's wording turned into a plain instruction. */
export function friendlyValidation(msg: string): string {
  let m: RegExpMatchArray | null;
  if ((m = msg.match(/at most (\d+) characters?/i))) return `Keep this under ${m[1]} characters.`;
  if ((m = msg.match(/at least (\d+) characters?/i))) return Number(m[1]) <= 1 ? "This is required." : `Enter at least ${m[1]} characters.`;
  if ((m = msg.match(/at most (\d+) items?/i))) return `Use at most ${m[1]}.`;
  if ((m = msg.match(/at least (\d+) items?/i))) return `Add at least ${m[1]}.`;
  if ((m = msg.match(/less than or equal to (\S+)/i))) return `Use ${m[1]} or less.`;
  if ((m = msg.match(/greater than or equal to (\S+)/i))) return `Use ${m[1]} or more.`;
  if (/field required|missing/i.test(msg)) return "This is required.";
  if (/valid email/i.test(msg)) return "Enter a valid email address.";
  if (/valid url|url scheme|relative url/i.test(msg)) return "Enter a full web address, e.g. https://example.com.";
  if (/valid integer|valid number/i.test(msg)) return "Enter a number.";
  if (/string does not match regex|pattern/i.test(msg)) return "That format isn't accepted.";
  const clean = msg.replace(/^Value error,\s*/i, "");
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/** FastAPI's validation list as { field: message }; nested fields use dots ("competitors.0.domain"). */
export function fieldErrors(detail: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(detail)) return out;
  for (const d of detail) {
    if (!d || typeof d !== "object") continue;
    const o = d as { loc?: unknown[]; msg?: string };
    if (!Array.isArray(o.loc) || !o.msg) continue;
    const path = (o.loc ?? []).filter((x) => x !== "body" && x !== "query" && x !== "path").map(String);
    if (!path.length) continue;
    const key = path.join(".");
    if (!out[key]) out[key] = friendlyValidation(o.msg);
    const top = path[0];
    if (!out[top]) out[top] = friendlyValidation(o.msg);
  }
  return out;
}

const STATUS_MESSAGES: Record<number, string> = {
  400: "That request wasn't quite right. Check what you entered and try again.",
  401: "Your session has ended. Please sign in again.",
  402: "This needs a higher package.",
  403: "You don't have access to this.",
  404: "We couldn't find that. It may have been moved or deleted.",
  408: "That took too long. Try again.",
  409: "That changed in the meantime. Refresh and try again.",
  413: "That's too large to upload.",
  422: "Some of what you entered isn't valid.",
  429: "Too many requests in a short time. Wait a moment and try again.",
  500: "Something went wrong on our side. It's been logged — try again in a moment.",
  502: "Rankbist is restarting or updating. Try again in a minute.",
  503: "Rankbist is busy or under maintenance. Try again in a minute.",
  504: "That took too long to answer. Try again in a minute.",
};

export function statusMessage(status: number): string {
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (status >= 500) return STATUS_MESSAGES[500];
  if (status >= 400) return "That didn't work. Try again.";
  return "Something went wrong. Try again.";
}

/** The server's "detail" as one readable sentence, or null. */
export function describeDetail(detail: unknown): string | null {
  if (typeof detail === "string") return detail.trim() || null;
  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{ loc: ["body", "url"], msg: "..." }, ...]
    const parts = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const o = d as { loc?: unknown[]; msg?: string; message?: string };
          const field = Array.isArray(o.loc) ? (o.loc ?? []).filter((x) => x !== "body" && x !== "query" && typeof x === "string").join(" ") : "";
          const msg = o.msg ? friendlyValidation(o.msg) : o.message || "";
          if (!msg) return "";
          const nice = field ? `${field.replace(/_/g, " ")}: ${msg}` : msg;
          return nice.charAt(0).toUpperCase() + nice.slice(1);
        }
        return "";
      })
      .filter(Boolean);
    return parts.length ? parts.slice(0, 3).join(" · ") : null;
  }
  if (detail && typeof detail === "object") {
    const o = detail as { message?: unknown; detail?: unknown };
    if (typeof o.message === "string") return o.message;
    if (o.detail !== undefined) return describeDetail(o.detail);
  }
  return null;
}

/** The last error a person saw, so the support chat can explain it
 *  (components/support). Kept in memory for 15 minutes; never sent anywhere
 *  unless they open the chat and write. */
export const LAST_ERROR_EVENT = "rb:last-error";
export function rememberError(message: string, status?: number) {
  if (typeof window === "undefined" || !message || status === 401) return;
  try {
    (window as unknown as { __rbLastError?: unknown }).__rbLastError = {
      message: message.slice(0, 500), status, at: Date.now(), page: window.location.pathname,
    };
    window.dispatchEvent(new CustomEvent(LAST_ERROR_EVENT));
  } catch {
    /* never let this break the request's own error */
  }
}
export function lastError(): { message: string; status?: number; page: string } | null {
  if (typeof window === "undefined") return null;
  const e = (window as unknown as { __rbLastError?: { message: string; status?: number; at: number; page: string } }).__rbLastError;
  return e && Date.now() - e.at < 15 * 60_000 ? e : null;
}

/** Build the error for a response that isn't ok. */
export async function errorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    const text = await res.text();
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null; // not JSON (an HTML error page from a proxy, for example)
  }
  const obj = body && typeof body === "object" ? (body as { detail?: unknown; error_id?: unknown; code?: unknown }) : null;
  const message = describeDetail(obj?.detail) ?? statusMessage(res.status);
  const code = obj?.code === "prelaunch" || obj?.code === "maintenance" ? obj.code : undefined;
  rememberError(message, res.status);
  if (code && typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(ACCESS_EVENT, { detail: { code, message } }));
    } catch {
      /* never let the notice break the request's own error */
    }
  }
  if (res.status === 403 || res.status === 402) leaveLostWorkspace(message);
  if ((res.status === 402 || res.status === 429) && typeof window !== "undefined" && !LOST.test(message)) {
    try {
      window.dispatchEvent(new CustomEvent(LIMIT_EVENT, { detail: { status: res.status, message } }));
    } catch {
      /* never let the notice break the request's own error */
    }
  }
  return new ApiError(message, res.status, typeof obj?.error_id === "string" ? obj.error_id : undefined, fieldErrors(obj?.detail), code);
}

/**
 * The browser still remembers a workspace this person can no longer open (it was deleted, they were removed, or the
 * owner's package lost workspaces). Every request in it would fail, so go back to the person's own account once,
 * with a note, instead of showing errors everywhere.
 */
const LOST = /^(You don't have access to that workspace|That workspace isn't available|Workspaces aren't included in this account's package)/;
function leaveLostWorkspace(message: string) {
  if (typeof window === "undefined" || !LOST.test(message)) return;
  try {
    if (!window.localStorage.getItem("rb_workspace")) return;
    window.localStorage.removeItem("rb_workspace");
    window.sessionStorage.setItem("rb_switched_to", "your own account (the workspace you were in isn't available any more)");
  } catch {
    return;
  }
  window.location.assign("/dashboard");
}

export function networkError(): ApiError {
  return new ApiError(
    typeof navigator !== "undefined" && navigator.onLine === false
      ? "You're offline. Check your connection and try again."
      : "Couldn't reach Rankbist. Check your connection and try again.",
    0,
  );
}

/** Read a successful response; an empty body (204) gives undefined instead of a crash. */
export async function readJson<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError("The server sent an answer we couldn't read. Try again.", res.status);
  }
}
